import * as React from "react";
import { Box, Grid, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ClockIcon, TrendUpIcon } from "@phosphor-icons/react";
import { MegaphoneIcon } from "@phosphor-icons/react/dist/ssr/Megaphone";
import { UsersIcon } from "@phosphor-icons/react/dist/ssr/Users";

import { useDashboardStats } from "@/hooks/api/use-dashboard";
import { useAnimation } from "@/components/core/animation-provider";
import { StatisticCard } from "@/components/statisticCard/statistic-card";
import { useContactsCount } from "@/hooks/api/use-contacts";

const animationDelay = 150;

export function Dashboard(): React.JSX.Element {
	const { setIsAnimated } = useAnimation();
	const { data: stats, isLoading } = useDashboardStats();


  const { data: contactsCount, isLoading: isContactsCountLoading } = useContactsCount();

	React.useEffect(() => {
		setTimeout(() => {
			setTimeout(() => {
				setIsAnimated(true);
			}, animationDelay * statisticCards.length);
		}, animationDelay * statisticCards.length);
	}, []);

	const statisticCards = React.useMemo(() => {
		const activeCampaigns = stats?.activeCampaigns?.current;
		const conversationRate = stats?.conversationRate?.current;
		const avgHandleTimeMinutes = stats?.avgHandleTimeMinutes;

		return [
			{
				title: "Total Contacts",
				value: contactsCount,
				icon: <UsersIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
			},
			{
				title: "Active Campaigns",
				value: activeCampaigns,
				icon: <MegaphoneIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
			},
			{
				title: "Contact Rate",
				value: conversationRate,
				icon: <TrendUpIcon color="var(--mui-palette-warning-main)" width={20} height={20} />,
			},
			{
				title: "Avg handle time",
				value: avgHandleTimeMinutes,
				icon: <ClockIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
				additonalInfo: "Minutes per call",
			},
		];
	}, [stats, contactsCount]);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Conversational AI Dashboard</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Overview of your AdaptiveX conversational AI platform performance
							</Typography>
						</Box>
					</Stack>

					<Grid container spacing={3}>
						{statisticCards.map((card, index) => (
							<Grid
								key={index}
								size={{
									md: 3,
									sm: 6,
									xs: 12,
								}}
							>
								<StatisticCard
									{...card}
									isLoading={isLoading || isContactsCountLoading}
									animationDelay={index * animationDelay}
									timeout={animationDelay * statisticCards.length}
									isLast={index === statisticCards.length - 1}
								/>
							</Grid>
						))}
					</Grid>
				</Stack>
			</Box>
		</React.Fragment>
	);
}
